import type { Trip } from '@viaja/contracts';
import { Button } from '@viaja/design-system';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { useSaveTrip } from '../../lib/queries';
import { Sheet } from '../../ui/Sheet';
import { TextAreaField, TextField } from '../../ui/TextField';
import { Notice } from '../../ui/feedback';

/** Criar ou editar uma viagem (nome, período, notas). */
export function TripFormSheet({
  open,
  trip,
  onClose,
  onSaved,
}: {
  open: boolean;
  trip?: Trip;
  onClose: () => void;
  onSaved?: (trip: Trip) => void;
}) {
  const save = useSaveTrip(trip?.id);
  const [name, setName] = useState('');
  const [startDate, setStart] = useState('');
  const [endDate, setEnd] = useState('');
  const [notes, setNotes] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: recarrega só ao abrir.
  useEffect(() => {
    if (!open) return;
    save.reset();
    setName(trip?.name ?? '');
    setStart(trip?.startDate ?? '');
    setEnd(trip?.endDate ?? '');
    setNotes(trip?.notes ?? '');
  }, [open]);

  const fields = save.error instanceof ApiError ? save.error.fields : {};
  const general = save.error && Object.keys(fields).length === 0 ? save.error.message : null;

  return (
    <Sheet open={open} title={trip ? 'Editar viagem' : 'Nova viagem'} onClose={onClose}>
      <form
        className="app-stack"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(
            { name, startDate: startDate || null, endDate: endDate || null, notes: notes || null },
            {
              onSuccess: (t) => {
                onSaved?.(t);
                onClose();
              },
            },
          );
        }}
      >
        <TextField
          label="Nome da viagem"
          placeholder="Ex.: Lisboa em maio"
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
          error={fields.name}
          data-autofocus
        />
        <TextField
          label="Começa em"
          type="date"
          value={startDate}
          onChange={(e) => setStart(e.target.value)}
          error={fields.startDate}
        />
        <TextField
          label="Termina em"
          type="date"
          min={startDate || undefined}
          value={endDate}
          onChange={(e) => setEnd(e.target.value)}
          error={fields.endDate}
        />
        <TextAreaField
          label="Notas"
          placeholder="Hotel, quem vai, ideias…"
          maxLength={500}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          error={fields.notes}
        />
        {general ? <Notice>{general}</Notice> : null}
        <Button type="submit" block disabled={save.isPending}>
          {trip ? 'Salvar viagem' : 'Criar viagem'}
        </Button>
      </form>
    </Sheet>
  );
}
